"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/shadcn-components/ui/dialog";
import { Button } from "@/shadcn-components/ui/button";
import { formatSignedPnl } from "@/vybe-trader-client/opened-trades";
import type { BulkCloseMode } from "@/vybe-trader-client/connection/client/VybeTraderClient";

const MODE_NOUN: Record<BulkCloseMode, string> = {
  all: "positions",
  profitable: "profitable positions",
  losing: "losing positions",
};

export interface BulkCloseConfirmDialogProps {
  open: boolean;
  mode: BulkCloseMode | null;
  count: number;
  realizedPnl: number;
  symbols: string[];
  currency: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * Shared count-confirm modal guarding the (irreversible) bulk-close verbs.
 * Drives both the desktop row context menu and the toolbar Actions menu.
 */
export function BulkCloseConfirmDialog({
  open,
  mode,
  count,
  realizedPnl,
  symbols,
  currency,
  onOpenChange,
  onConfirm,
}: BulkCloseConfirmDialogProps) {
  const noun = mode ? MODE_NOUN[mode] : "positions";
  const pnlClass = realizedPnl >= 0 ? "text-blue-400" : "text-red-400";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-[13px]">Close {noun}</DialogTitle>
          <DialogDescription className="text-[11px]">
            Bulk close is irreversible — this sends {count}× close request
            {count === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5 py-1 font-mono text-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Positions to close</span>
            <span className="font-semibold tabular-nums">{count}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Symbols</span>
            <span className="max-w-[60%] truncate text-right">
              {symbols.length > 0 ? symbols.join(", ") : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Est. realized P&amp;L</span>
            <span className={`font-semibold tabular-nums ${pnlClass}`}>
              {formatSignedPnl(realizedPnl)} {currency}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px]"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="h-7 text-[11px]"
            disabled={count === 0}
            onClick={onConfirm}
          >
            Close {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
